import React, { useState, useCallback, useMemo } from 'react';

import { StyledLabel, StyledRequiredSpan, StyledSelect, ErrorMessage } from '@/components/Form/styles';
import { StyledFlexColumn } from '@/layout/FlexLayout';

import type { ChangeEvent, SelectHTMLAttributes } from 'react';

export type OptionValue = string | number | undefined;
export type Option<Value extends OptionValue> = {
  value: Value;
  label?: string;
};

interface SelectProps<T> extends SelectHTMLAttributes<HTMLSelectElement> {
  required?: boolean;
  label?: string;
  fieldKey: T;
  error?: string;
  showError?: boolean;
  disableErrors?: boolean;
  options: Option<OptionValue>[];
  handleValue: (key: T, value: string) => void;
}

export function Select<T>({
  required,
  error,
  showError,
  disableErrors,
  fieldKey,
  handleValue,
  label,
  value = '',
  disabled,
  options,
  ...props
}: SelectProps<T>) {
  const [hasTouched, setHasTouched] = useState(false);
  const selectedValue = useMemo(() => value || '', [value]);

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      handleValue(fieldKey, event.currentTarget.value);
    },
    [fieldKey, handleValue],
  );

  return (
    <StyledFlexColumn>
      {label && (
        <StyledLabel>
          {label} {required && <StyledRequiredSpan>*</StyledRequiredSpan>}
        </StyledLabel>
      )}
      <StyledSelect
        onBlur={() => setHasTouched(true)}
        onChange={handleOnChange}
        value={selectedValue}
        disabled={disabled}
        {...props}
      >
        <option value="" disabled>
          Select an {label}
        </option>

        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option?.label || option.value}
          </option>
        ))}
      </StyledSelect>
      {!disableErrors && <ErrorMessage>{(hasTouched || showError) && error}</ErrorMessage>}
    </StyledFlexColumn>
  );
}
